import type { IBannerDesign } from '@/sanity/lib/definitions';

/**
 * Countdown window of a Top Banner / Promo Banner document (New UI 2026
 * design, see sanity/globals/BannerDesign.ts).
 *
 * The countdown is shown while `showCountdown` is on AND now is inside
 * [countdownStartDate, countdownEndDate). A missing start means "from now on";
 * a missing or invalid end means "no countdown" — never a countdown to
 * nothing. Once the end passes the countdown disappears and the banner stays.
 */

export interface TimeLeft {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

export function isBannerDesignV1(config: Pick<IBannerDesign, 'design'> | null | undefined): boolean {
  return config?.design === 'v1';
}

export type BannerDesignValue = NonNullable<IBannerDesign['design']>;

/**
 * The design a banner document renders with. Every document saved before the
 * switch existed has no value, and so does any value the code does not know
 * (a skin from a newer schema on older code): both render Classic.
 */
export function getBannerDesign(
  config: Pick<IBannerDesign, 'design'> | null | undefined,
): BannerDesignValue {
  const design = config?.design;
  return design === 'v1' || design === 'halloween' || design === 'blackMonth' ? design : 'default';
}

/** The moment the countdown counts down to, or null while it must stay hidden. */
export function getBannerCountdownTarget(
  config: IBannerDesign | null | undefined,
  now: number = Date.now(),
): Date | null {
  if (!config?.showCountdown || !config.countdownEndDate) return null;
  const end = new Date(config.countdownEndDate);
  if (!Number.isFinite(end.getTime()) || end.getTime() <= now) return null;
  if (config.countdownStartDate) {
    const start = new Date(config.countdownStartDate).getTime();
    if (Number.isFinite(start) && start > now) return null;
  }
  return end;
}

export function getTimeLeft(target: Date, now: number = Date.now()): TimeLeft {
  const totalSeconds = Math.max(0, Math.floor((target.getTime() - now) / 1000));
  return {
    days: Math.floor(totalSeconds / 86_400),
    hours: Math.floor((totalSeconds % 86_400) / 3_600),
    minutes: Math.floor((totalSeconds % 3_600) / 60),
    seconds: totalSeconds % 60,
  };
}

export function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

/** Unit labels per site language (standard time units — kept in code, as the Summer Sale popup does). */
export const COUNTDOWN_LABELS: Record<string, [string, string, string, string]> = {
  en: ['DAYS', 'HRS', 'MINS', 'SECS'],
  de: ['TAGE', 'STD', 'MIN', 'SEK'],
  br: ['DIAS', 'HRS', 'MIN', 'SEG'],
};

export function getCountdownLabels(language: string | undefined): [string, string, string, string] {
  return COUNTDOWN_LABELS[language ?? 'en'] ?? COUNTDOWN_LABELS.en;
}
